import { useState } from 'react';
import { toPng } from 'html-to-image';
import { Document, Page, Text, View, Image, StyleSheet, Font, pdf } from '@react-pdf/renderer';

const StudentProgressPDF = ({ student, currentPeriod, selectedPeriods, bundlessWithIntervention, progressTableRef, progressBarChartRef }) => {
    const [pdfLoading, setPdfLoading] = useState(false);

    Font.register({ family: 'Poppins', src: 'https://fonts.gstatic.com/s/poppins/v21/pxiEyp8kv8JHgFVrFJA.ttf' });

    // Capture both images in parallel
    const captureImages = async () => {
        const [tableUrl, chartUrl] = await Promise.all([
            toPng(progressTableRef.current, { backgroundColor: '#fff', pixelRatio: 2 }),
            toPng(progressBarChartRef.current, { backgroundColor: '#fff', pixelRatio: 2 }),
        ]);
        return { tableUrl, chartUrl };
    };

    // PDF Styles
    const pdfStyles = StyleSheet.create({
        title: { fontFamily: 'Poppins', fontWeight: 700, color: '#3C5063' },
        text: { fontFamily: 'Poppins', fontWeight: 700, color: '#3C5063', fontSize: 10 },
        textUnderline: { fontFamily: 'Poppins', fontWeight: 700, color: '#3C5063', fontSize: 12, textDecoration: 'underline' },
        page: { paddingTop: 40, paddingBottom: 70 },
        section: { marginLeft: '30px', marginRight: '30px', paddingBottom: '5px' },
        image: { backgroundColor: '#fff', width: '100%', objectFit: 'contain', marginVertical: 10 },
        imageCont: { maxHeight: '75vh', maxWidth: '100vw' },
        detailsSection: { paddingLeft: 3, paddingRight: '130px' },
        singleOutcome: { marginTop: 10 },
        logo: { width: 100, position: 'absolute', top: '0px', right: 0 },
        footer: { position: 'absolute', top: '87vh', left: 0, maxWidth: '100vw' },
        footerText: { fontSize: '8pt', color: '#5c5c5c' }
    });

    // The PDF document component
    const PdfDocument = ({ tableUrl, chartUrl }) => (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                <View style={pdfStyles.section}>
                    <Image style={pdfStyles.logo} src={require('../../assets/SCP-logo.jpg')} />
                    <View style={pdfStyles.detailsSection}>
                        <Text style={pdfStyles.title}>
                            {student.name} {currentPeriod && ` - Year ${currentPeriod.year_group}`} {student.school?.name && ` - ${student.school.name}`}
                        </Text>
                        <Text style={pdfStyles.text}>School - {student.school?.name}</Text>
                        <Text style={pdfStyles.text}>
                            Current Intervention lead - {currentPeriod?.tutor_group?.tutor?.name || 'N/A'}
                        </Text>
                        <Text style={pdfStyles.text}>
                            Intervention Type - {currentPeriod?.intervention_type
                                ? currentPeriod.intervention_type.charAt(0).toUpperCase() + currentPeriod.intervention_type.slice(1)
                                : 'N/A'}
                        </Text>
                        <Text style={pdfStyles.text}>
                            Reporting Periods -{" "}
                            {selectedPeriods?.map((p, i) => (
                                <Text key={i} style={pdfStyles.text}>
                                    {p.period_name} {p.calendar_year}{i !== selectedPeriods.length - 1 && ", "}
                                </Text>
                            ))}
                        </Text>
                    </View>
                    <View style={pdfStyles.imageCont}>
                        {tableUrl && <Image style={pdfStyles.image} src={tableUrl} />}
                    </View>
                    <View style={pdfStyles.footer} fixed>
                        <Text style={pdfStyles.footerText}>School Consultancy Programme ...</Text>
                    </View>
                </View>
            </Page>
            <Page size="A4" style={pdfStyles.page}>
                <View style={pdfStyles.section}>
                    <Image style={pdfStyles.logo} src={require('../../assets/SCP-logo.jpg')} />
                    <View style={pdfStyles.detailsSection}>
                        <Text style={pdfStyles.title}>{student.name}</Text>
                    </View>
                    <View style={pdfStyles.imageCont}>
                        {chartUrl && <Image style={pdfStyles.image} src={chartUrl} />}
                    </View>
                    <View wrap>
                        <Text style={pdfStyles.title}>Progress Outcome Statements</Text>
                        <Text style={pdfStyles.text}>Outcomes for bundles completed in period: {currentPeriod.period_name} {currentPeriod.calendar_year}</Text>
                        {bundlessWithIntervention?.map((bundle, idx) => (
                            <View key={idx} style={pdfStyles.singleOutcome} wrap={false}>
                                <Text style={pdfStyles.textUnderline}>{bundle.levelLabel} {bundle.label}</Text>
                                <Text style={pdfStyles.text}>{bundle.outcome}</Text>
                            </View>
                        ))}
                    </View>
                    <View style={pdfStyles.footer} fixed>
                        <Text style={pdfStyles.footerText}>School Consultancy Programme ...</Text>
                    </View>
                </View>
            </Page>
        </Document>
    );

    // Generate + download in one click
    const handleDownload = async () => {
        setPdfLoading(true);
        try {
            const { tableUrl, chartUrl } = await captureImages();
            const blob = await pdf(<PdfDocument tableUrl={tableUrl} chartUrl={chartUrl} />).toBlob();

            // create a temporary link to download
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${student.name}_progress.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error generating PDF:", err);
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <button onClick={handleDownload} className="pdfButton" disabled={pdfLoading}>
            {pdfLoading ? "Generating PDF..." : "Download PDF"}
        </button>
    );
};

export default StudentProgressPDF;